import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AuthPromo from "@/components/AuthPromo";
import { ArrowRight, ArrowLeft, Loader2, Mail, CheckCircle2 } from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/fd1c566f5b8864f6832e4d35cf9140f3939f77c249aa2aa7f7d74ab738a59602.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !/.+@.+\..+/.test(email)) { toast.error("Enter a valid email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setDevLink(data.dev_link || null);
      setSubmitted(true);
      if (data.email_sent) toast.success("Reset link sent (if the account exists).");
      else if (data.dev_link) toast.warning("Email send unavailable — use the on-screen reset link.");
      else toast.success("If that account exists, a reset link was sent.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-[#F0ECE0] flex flex-col">
        <div className="px-8 md:px-14 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="forgot-logo">
            <Logo variant="wordmark" size={36} />
          </Link>
          <Link to="/login" className="text-sm text-[#4F4D45] hover:text-[#1B3526]" data-testid="forgot-link-login">Back to sign in</Link>
        </div>
        <div className="flex-1 px-8 md:px-14 pt-16 pb-10 flex flex-col">
          {!submitted ? (
            <form onSubmit={submit} className="max-w-md w-full">
              <div className="w-12 h-12 rounded-xl bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center mb-6">
                <Mail className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Forgot your password?</h2>
              <p className="text-[#4F4D45] mt-3">Enter the email you used to open your FinVault account. We'll send you a secure link to set a new password.</p>
              <div className="mt-8">
                <Label className="text-eyebrow text-[#4F4D45]">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-input-email" className="mt-1" />
              </div>
              <Button type="submit" disabled={loading} className="mt-8 bg-[#1B3526] hover:bg-[#0F2517] w-full sm:w-auto" data-testid="forgot-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-xs text-[#4F4D45] mt-8 leading-relaxed">For your security, we don't reveal whether an email is registered. You'll only receive a link if an account exists.</p>
            </form>
          ) : (
            <div className="max-w-md w-full">
              <div className="w-12 h-12 rounded-xl bg-[#264C39]/10 text-[#264C39] flex items-center justify-center mb-6">
                <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Check your inbox</h2>
              <p className="text-[#4F4D45] mt-3">If an account exists for <strong className="text-[#0A0A08]">{email}</strong>, a password reset link is on its way. The link expires in 1 hour.</p>
              {devLink && (
                <div className="mt-5 p-4 rounded-lg bg-[#B58957]/20 border border-[#B58957] text-sm" data-testid="forgot-dev-link">
                  Email send unavailable. Use this reset link:
                  <div className="mt-2 break-all"><a href={devLink} className="text-[#1B3526] underline">{devLink}</a></div>
                </div>
              )}
              <div className="mt-8 flex gap-3">
                <Button onClick={() => navigate("/login")} variant="outline" className="border-[#1B3526] text-[#1B3526] hover:bg-[#1B3526] hover:text-white" data-testid="forgot-go-login">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
                </Button>
                <Button onClick={() => { setSubmitted(false); setDevLink(null); }} variant="ghost" data-testid="forgot-resend">Send again</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <AuthPromo />
      </div>
    </div>
  );
}
